"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import { BottomNav } from "../../_components/bottom-nav";
import { ExploreFooter } from "../../_components/explore-footer";
import { useExploreRoute } from "../../_lib/explore-route-context";
import type { ToolCategoryGridCard, ToolCategoryGridData } from "./data";

export function ToolCategoryGridPage({ data }: { data: ToolCategoryGridData }) {
  const { exploreBase } = useExploreRoute();
  const { categories, totalSoftwareProducts } = data;

  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) {
      return categories;
    }
    const q = query.trim().toLowerCase();
    return categories.filter(
      (c) =>
        c.displayName.toLowerCase().includes(q) ||
        (c.description ?? "").toLowerCase().includes(q)
    );
  }, [categories, query]);

  return (
    <main
      className="pb-[calc(var(--explore-bottom-nav-h)+var(--explore-safe-bottom)+16px)] lg:pb-0"
      id="main-content"
      tabIndex={-1}
    >
      <div className="mx-auto w-full max-w-[1320px] px-4 sm:px-6">
        <div className="py-6 pb-4">
          <nav
            aria-label="Breadcrumb"
            className="mb-3 text-[0.8rem] text-[var(--explore-text-muted)]"
          >
            <Link
              className="hover:text-[var(--explore-text-secondary)]"
              href={`${exploreBase}/tools`}
            >
              All Tools
            </Link>
            <span className="mx-2">›</span>
            <span className="text-[var(--explore-text-secondary)]">
              Categories
            </span>
          </nav>
          <h1
            className="mb-1.5 font-bold text-[var(--explore-text-primary)]"
            style={{
              fontFamily: "var(--explore-card-title-stack)",
              fontSize: "32px",
              lineHeight: "1.2",
              fontWeight: 700,
            }}
          >
            Tool Categories
          </h1>
          <p className="max-w-[640px] text-[1rem] text-[var(--explore-text-secondary)]">
            Browse AI tools by category. Click a category to see every tool
            tracked by the AI Primer team in that space.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-8 pb-6">
          <div className="flex items-baseline gap-1.5">
            <span className="font-bold text-[1.5rem] text-[var(--explore-text-primary)]">
              {categories.length}
            </span>
            <span className="text-[0.9rem] text-[var(--explore-text-muted)]">
              categories
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-bold text-[1.5rem] text-[var(--explore-text-primary)]">
              {totalSoftwareProducts}
            </span>
            <span className="text-[0.9rem] text-[var(--explore-text-muted)]">
              tools
            </span>
          </div>
          <Link
            className="ml-auto text-[0.85rem] text-[var(--explore-accent-teal)] hover:underline"
            href={`${exploreBase}/tools`}
          >
            View flat catalog →
          </Link>
        </div>

        <div className="pb-6">
          <div className="relative max-w-[360px]">
            <svg
              aria-hidden="true"
              className="-translate-y-1/2 absolute top-1/2 left-3.5 h-4 w-4 text-[var(--explore-text-muted)]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              className="w-full rounded-full border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] py-2 pr-4 pl-10 text-[0.85rem] text-[var(--explore-text-secondary)] outline-none transition-all focus:border-[var(--explore-accent-teal)] focus:ring-[3px] focus:ring-[var(--explore-focus-ring)]"
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search categories..."
              type="text"
              value={query}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 pb-12 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((category) => (
            <CategoryCard
              category={category}
              exploreBase={exploreBase}
              key={category.slug}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="py-12 text-center text-[var(--explore-text-muted)]">
            No categories match your search.
          </div>
        )}
      </div>

      <ExploreFooter />
      <BottomNav />
    </main>
  );
}

function CategoryCard({
  category,
  exploreBase,
}: {
  category: ToolCategoryGridCard;
  exploreBase: string;
}) {
  return (
    <Link
      className="hover:-translate-y-0.5 relative flex h-full flex-col rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] p-5 no-underline shadow-[var(--explore-inline-shadow)] transition-all hover:border-[var(--explore-border)] hover:shadow-[var(--explore-shadow-hover)]"
      href={`${exploreBase}/tools/categories/${encodeURIComponent(category.slug)}`}
    >
      <div className="mb-3 flex items-start justify-between">
        <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-[12px] bg-[var(--explore-bg-highlight)] text-[1.6rem]">
          {category.emoji ?? category.displayName.charAt(0)}
        </div>
        <div className="flex items-baseline gap-1 rounded-full bg-[var(--explore-bg-highlight)] px-2.5 py-1">
          <span className="font-bold text-[0.85rem] text-[var(--explore-text-primary)]">
            {category.productCount}
          </span>
          <span className="text-[0.7rem] text-[var(--explore-text-muted)]">
            {category.productCount === 1 ? "tool" : "tools"}
          </span>
        </div>
      </div>

      <div className="mb-2 font-bold text-[1.05rem] text-[var(--explore-text-primary)]">
        {category.displayName}
      </div>

      {category.description && (
        <p className="mb-3 line-clamp-3 text-[0.8rem] text-[var(--explore-text-secondary)] leading-[1.5]">
          {category.description}
        </p>
      )}

      {category.featuredIcons.length > 0 && (
        <div className="mt-auto flex items-center gap-1.5 pt-2">
          {category.featuredIcons.map((iconUrl) => (
            <div
              className="h-7 w-7 overflow-hidden rounded-md border border-[var(--explore-border-light)] bg-[var(--explore-bg)]"
              key={`${category.slug}-icon-${iconUrl}`}
            >
              <Image
                alt=""
                className="h-full w-full object-cover"
                height={28}
                src={iconUrl}
                width={28}
              />
            </div>
          ))}
        </div>
      )}
    </Link>
  );
}
